import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  getCountries,
  Country,
} from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { ChevronDown, Search, Check, AlertCircle, Globe } from 'lucide-react';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';


const getCountryName = (code: string): string => {
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
};

const FlagIcon: React.FC<{ country: string; size?: number }> = ({
  country,
  size = 18,
}) => {
  const Comp = (flags as any)[country];
  if (!Comp) return null;
  return (
    <span
      className="inline-flex items-center justify-center rounded overflow-hidden flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.08)]"
      style={{ width: size + 6, height: size - 2 }}
    >
      <Comp title={country} />
    </span>
  );
};

/* ─── Types ─────────────────────────────────────────────────── */

interface CountrySelectFieldProps {
  /** 2-letter ISO code, e.g. "IN". */
  value: string;
  /** Emits the 2-letter ISO code. */
  onChange: (code: string) => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}

/* ─── Component ─────────────────────────────────────────────── */

const CountrySelectField: React.FC<CountrySelectFieldProps> = ({
  value,
  onChange,
  label = 'Country',
  error,
  required = false,
  disabled = false,
  readOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { triggerRef, position } = useDropdownPosition(isOpen, 4, 140);

  const countries = useMemo(
    () =>
      getCountries()
        .map((c) => ({ code: c, name: getCountryName(c) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    const q = searchQuery.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [countries, searchQuery]);

  const selectedCountry = value?.toUpperCase() as Country | '';
  const selectedName = selectedCountry ? getCountryName(selectedCountry) : '';
  const hasValue = !!selectedCountry;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const panelEl = document.getElementById('country-select-panel');
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        (!panelEl || !panelEl.contains(target))
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, triggerRef]);

  // Auto-focus search when opened
  useEffect(() => {
    if (isOpen) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [isOpen]);

  const toggleOpen = () => {
    if (disabled || readOnly) return;
    setIsOpen((prev) => !prev);
    if (isOpen) setSearchQuery('');
  };

  const handleSelect = (code: string) => {
    onChange(code);
    setIsOpen(false);
    setSearchQuery('');
  };

  const dropdownStyle: React.CSSProperties = position.ready
    ? position.direction === 'below'
      ? {
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: Math.max(position.width, 280),
        maxWidth: 380,
        maxHeight: position.maxHeight,
        zIndex: 9999,
      }
      : {
        position: 'fixed',
        bottom: position.bottom,
        left: position.left,
        width: Math.max(position.width, 280),
        maxWidth: 380,
        maxHeight: position.maxHeight,
        zIndex: 9999,
      }
    : { display: 'none' };

  const dropdown = isOpen
    ? ReactDOM.createPortal(
      <div
        id="country-select-panel"
        style={dropdownStyle}
        className="rounded-xl border border-slate-200 dark:border-white/[0.10] bg-white dark:bg-surface-dark-400 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_-4px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden"
      >
        {/* Search */}
        <div className="flex-shrink-0 p-2 border-b border-slate-100 dark:border-white/[0.07] bg-slate-50 dark:bg-surface-dark-300">
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-white dark:bg-surface-dark-300 rounded-lg border border-slate-200 dark:border-white/[0.10] focus-within:border-brand-400 dark:focus-within:border-brand-400/60">
            <Search size={13} className="text-slate-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 min-w-0 text-[13px] bg-transparent outline-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Country list */}
        <div className="flex-1 overflow-y-auto py-0.5 min-h-0">
          {filteredCountries.map((country) => {
            const isSelected = country.code === selectedCountry;
            return (
              <button
                key={country.code}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(country.code)}
                className={[
                  'w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px]',
                  isSelected
                    ? 'bg-brand-50 dark:bg-brand-500/10'
                    : 'hover:bg-slate-50 dark:hover:bg-surface-dark-200',
                ].join(' ')}
              >
                <FlagIcon country={country.code} />
                <span
                  className={`flex-1 text-[13px] ${isSelected ? 'font-semibold text-brand-700 dark:text-brand-300' : 'text-slate-700 dark:text-slate-200'
                    }`}
                >
                  {country.name}
                </span>
                <span className="text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                  {country.code}
                </span>
                {isSelected && (
                  <Check size={13} className="text-brand-500 ml-0.5 shrink-0" />
                )}
              </button>
            );
          })}

          {filteredCountries.length === 0 && (
            <div className="px-4 py-8 text-center">
              <p className="text-[13px] text-slate-400">No countries found</p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                Try a different search term
              </p>
            </div>
          )}
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="w-full flex flex-col gap-1">

      {/* ── Label (above — matches FloatingLabelInput pattern) ── */}
      <label
        className={[
          'text-[11.5px] font-medium leading-none select-none',
          error ? 'text-red-500' : 'text-slate-500 dark:text-slate-400',
        ].join(' ')}
      >
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>

      {/* ── Trigger button ── */}
      <div ref={triggerRef}>
        <button
          type="button"
          onClick={toggleOpen}
          disabled={disabled}
          className={[
            'relative w-full flex items-center rounded-lg border bg-white dark:bg-surface-dark-300',
            'h-[40px] text-left transition-all duration-150',
            error
              ? 'border-red-300 dark:border-red-500/50 focus:border-red-400 focus:ring-2 focus:ring-red-500/[0.08]'
              : isOpen
                ? 'border-brand-400 dark:border-brand-400/60 ring-2 ring-brand-500/[0.08]'
                : 'border-slate-200 dark:border-white/[0.12] hover:border-slate-300 dark:hover:border-white/[0.20]',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          {/* Flag / globe icon */}
          <div className="flex items-center pl-3 shrink-0">
            {hasValue ? (
              <FlagIcon country={selectedCountry as string} size={18} />
            ) : (
              <Globe size={14} className="text-slate-400 dark:text-slate-500" />
            )}
          </div>

          {/* Selected value */}
          <div className="flex-1 min-w-0 px-2.5">
            {hasValue ? (
              <span className="block truncate text-[13.5px] font-normal text-slate-800 dark:text-slate-100">
                {selectedName}
              </span>
            ) : (
              <span className="block text-[13.5px] text-slate-400 dark:text-slate-500">
                Select country…
              </span>
            )}
          </div>

          {/* Chevron */}
          {!readOnly && !disabled && (
            <div className="pr-3 shrink-0">
              <ChevronDown
                size={14}
                className={`text-slate-400 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          )}
        </button>
      </div>

      {dropdown}

      {/* ── Error ── */}
      {error && (
        <div className="flex items-center gap-1 mt-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default CountrySelectField;
